import clsx from 'clsx'

import { getStatusLabel } from '../../constants/orderStatus'

interface StatusPillStyle {
  bg: string
  text: string
  dot: string
  border: string
  pulse: boolean
}

const statusPillStyles: Record<string, StatusPillStyle> = {
  awaiting_approval: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    dot: 'bg-amber-500',
    border: 'border-amber-200',
    pulse: true,
  },
  pending_approval: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    dot: 'bg-amber-500',
    border: 'border-amber-200',
    pulse: true,
  },
  approved: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500',
    border: 'border-emerald-200',
    pulse: false,
  },
  // "Submitting" — order is in the retry loop before the supplier acks.
  // Distinct label / colour from "Sent to supplier" below; no pulse.
  started_processing: {
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    dot: 'bg-sky-500',
    border: 'border-sky-200',
    pulse: false,
  },
  // "Sent to supplier" — order is with the supplier, waiting for their ack.
  awaiting_supplier: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    dot: 'bg-blue-500',
    border: 'border-blue-200',
    pulse: true,
  },
  // Both share the "With supplier" label — same colour as processing below.
  received_by_supplier: {
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    dot: 'bg-indigo-500',
    border: 'border-indigo-200',
    pulse: true,
  },
  processing: {
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    dot: 'bg-indigo-500',
    border: 'border-indigo-200',
    pulse: true,
  },
  ready_to_collect: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500',
    border: 'border-emerald-200',
    pulse: false,
  },
  completed: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500',
    border: 'border-emerald-200',
    pulse: false,
  },
  internal_decline: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    dot: 'bg-red-500',
    border: 'border-red-200',
    pulse: false,
  },
  rejected: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    dot: 'bg-red-500',
    border: 'border-red-200',
    pulse: false,
  },
  fulfillment_declined: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    dot: 'bg-red-500',
    border: 'border-red-200',
    pulse: false,
  },
  supplier_cancelled: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    dot: 'bg-red-500',
    border: 'border-red-200',
    pulse: false,
  },
  withdrawn: {
    bg: 'bg-gray-50',
    text: 'text-gray-500',
    dot: 'bg-gray-400',
    border: 'border-gray-200',
    pulse: false,
  },
  cancelled: {
    bg: 'bg-gray-50',
    text: 'text-gray-500',
    dot: 'bg-gray-400',
    border: 'border-gray-200',
    pulse: false,
  },
}

const FALLBACK_STYLE: StatusPillStyle = {
  bg: 'bg-gray-50',
  text: 'text-gray-500',
  dot: 'bg-gray-400',
  border: 'border-gray-200',
  pulse: false,
}

interface AdminOrderStatusPillProps {
  status: string
  className?: string
  size?: 'sm' | 'md'
}

export function AdminOrderStatusPill({
  status,
  className,
  size = 'sm',
}: AdminOrderStatusPillProps) {
  const style = statusPillStyles[status] || FALLBACK_STYLE
  const label = getStatusLabel(status)

  return (
    <span
      className={clsx(
        'inline-flex items-center font-semibold border',
        size === 'md'
          ? 'gap-2 px-3 py-1 rounded-lg text-xs'
          : 'gap-1.5 px-2 py-0.5 rounded-md text-[11px]',
        style.bg,
        style.text,
        style.border,
        className,
      )}
    >
      <span className={clsx('relative flex', size === 'md' ? 'h-2 w-2' : 'h-1.5 w-1.5')}>
        {style.pulse && (
          <span
            className={clsx(
              'animate-ping absolute inline-flex h-full w-full rounded-full opacity-60',
              style.dot,
            )}
          />
        )}
        <span className={clsx('relative inline-flex h-full w-full rounded-full', style.dot)} />
      </span>
      {label}
    </span>
  )
}
